import { type Action, createAsyncThunk, type ThunkDispatch } from '@reduxjs/toolkit';
import type { Memory, VariousMemoryIdRefs } from '@timelyapp/integration-cloud-api-spec';
import { auditsApi } from 'api/audits/audits.api';
import * as hoursApi from 'api/hours/hours.api';
import reportsApi from 'api/reports/reports.api';
import * as summaryApi from 'api/users/summary.api';
import { MAX_HOUR_NOTE_CHARS } from 'constants/hour';
import { HAS_CREATED_HOUR } from 'constants/user';
import { errorMessageFactory } from 'factories/api';
import {
  hourDraftFactory,
  normalizedHourDraftToXhrFactory,
  resetHourStateProps,
} from 'factories/hours';
import { xor } from 'lodash-es';
import { DateTime } from 'luxon';
import {
  fetchProjectId,
  fetchProjectIdSmartly,
  insertInferredProjectPermissions,
} from 'redux/modules/projects.actions';
import { showAccountExpiredMessage } from 'redux/modules/subscriptions.actions';
import { showToast } from 'redux/modules/toasts.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import {
  getAccountCurrency,
  getAccountFeatures,
  getAllProjectsLoggingEnabled,
  getHasHourStatesFeature,
  getIsSubscriptionExpiredOrTrialEnded,
} from 'redux/selectors/accounts';
import { getCalendarUserId } from 'redux/selectors/calendar';
import { getUrlParameters } from 'redux/selectors/calendar.filter';
import { getCurrenciesById } from 'redux/selectors/currencies';
import {
  getHourEntities,
  getHourResult,
  getHours,
  getHourXhrUIState,
  getUnprocessedHours,
} from 'redux/selectors/hours';
import {
  getDraftDurationMode,
  getEnrichedHourDraft,
  getHourDraft,
  getHourEditorDurationSuggestions,
  getPreferredAutoNoteStrategy,
  getSelectedMemoryEntriesChronologically,
  getShouldFetchProjectDetails,
} from 'redux/selectors/hours.draft';
import { getHourDraftId, getHourDraftUserId } from 'redux/selectors/hours.draft.base';
import {
  getProjectById,
  getProjectPermissionsMap,
  getProjectVariantMap,
} from 'redux/selectors/projects';
import { getUserLedUserIds } from 'redux/selectors/teams';
import { getTimelineSelection } from 'redux/selectors/timeline';
import { getActualDate } from 'redux/selectors/timeline.memories';
import { getHasEnabledTic, getIsUserAdmin, getUserRoleId } from 'redux/selectors/user';
import { getUserEntities } from 'redux/selectors/users';
import { enrichProjectVariant, getFullProjectOrNull } from 'redux/selectors/utils/projects';
import type { IState } from 'redux/state';
import type { ForecastFilter } from 'routes/forecast/api/forecast.api';
import { fetchForecastId } from 'routes/forecast/redux/modules/forecast.actions';
import { getEnrichedForecasts } from 'routes/forecast/redux/modules/forecast.selectors';
import { HOUR_SCHEMA_KEY } from 'schema/hours';
import { BATCH_CREATED_PLANNED_ENTRIES } from 'tracking/constants';
import TimelyAnalytics from 'tracking/timely-analytics';
import type { HourAnalyticsContext } from 'types/analytics';
import type {
  Hour,
  HourDraft,
  HourTimestamp,
  MoveOrCopyHourPayload,
  NormalizedHourToApi,
} from 'types/hour';
import type { LightProject, Project } from 'types/project';
import { TagSettings } from 'types/project';
import type { Thunk } from 'types/redux';
import type { EntityDataQuery } from 'types/reports';
import type { Merge } from 'types/utility';
import { calculateCost } from 'utils';
import { finalizeHourAnalyticsContext } from 'utils/analytics-context';
import { fetchInBatchesOf } from 'utils/api';
import { getCurrentMomentOnDay, isAfter } from 'utils/date-helper';
import debug from 'utils/debug';
import { calculateDuration } from 'utils/durations';
import * as hourUtils from 'utils/hours';
import { hoursResequencer } from 'utils/hours.resequencer';
import { fromISO } from 'utils/luxon-helpers';
import { removeIllegalLabelIds } from 'utils/project';
import { positionOrHighest } from 'utils/resequencer';
import { pluralize, truncateText } from 'utils/text-helper';
import { dayTools } from 'utils/time';
import { getToggleTimerChanges } from 'utils/timers';
import {
  adjustTimestampsToDuration,
  calculateDurationFromTimestamps,
  mergeOverlappingTimestampsContainingTicMemories,
  mutateTimestampRange,
} from 'utils/timestamps';
import { notNull } from 'utils/typesafe';
import { safelyDelayRetrainAiModel } from '~domains/ai/predictions/redux/predictions.actions';
import { getIsTicAiCurrentlyActive } from '~domains/ai/redux/ai.availability.selectors';

// Lazy import to break circular dependency: hours.actions → calendar.actions → suggestions.actions → hours.actions
const getSetCalendarDate = () => import('./calendar.actions').then(m => m.setCalendarDate);

import { getHourStateById } from './hourStates';

// Lazy import to break circular dependency: hours.actions ↔ hours.actions.copymove
const getMoveHourDraftToDate = () =>
  import('./hours.actions.copymove').then(m => m.moveHourDraftToDate);

import * as types from './hours.types';
import {
  fetchedHourHistory,
  hoursXhrStatusModule,
  OPTIMISTIC_HOUR_IDENTIFIER,
} from './hours.types';
import { fetchedFullProjects, touchProjectPermission } from './projects.types';
import { acceptSuggestionId } from './suggestions.types';
import type { BulkUpdateResponse } from './superreports.types';
import { getTicTasks } from './tic/tasks/tic.tasks.slice';
import { getTicAndLegacyMemoriesAsMemoryGroups } from './tic/tic.memories.selectors';
import { clearSelectEntryIds, ensureEntryIdsAreNotMarkedAsSpam } from './timeline.actions';

export const TODAY = DateTime.now().toISODate();

const { wrapFetching, wrapSaving } = hoursXhrStatusModule;

export function fetchHourHistory(): Thunk {
  return async (dispatch, getState) => {
    const id = getHourDraftId(getState());
    if (!id) return;

    try {
      const history = await dispatch(wrapFetching(auditsApi.getHourHistory(id)));
      dispatch(fetchedHourHistory(history));
    } catch (e) {
      dispatch(showToast({ message: errorMessageFactory(e), type: 'danger' }));
    }
  };
}

export function safelyDeleteLocalHourById(id: number): Thunk<boolean> {
  return (dispatch, getState) => {
    const hourExistsInLocalStore = !!getHourEntities(getState())[id];

    if (hourExistsInLocalStore) {
      dispatch(types.deleteHour(id));
    }
    return hourExistsInLocalStore;
  };
}

export function deleteHourId(id: number): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const hour = getHourEntities(getState())[id];

    // Only attempt to delete server-side if hour exists locally
    if (!hour) return;

    try {
      dispatch(types.deleteHour(id));
      await dispatch(wrapSaving(hoursApi.deleteHour(id)));
      dispatch(types.savedHour());
    } catch (error) {
      dispatch(
        showToast({
          message: `Failed to delete entry – ${errorMessageFactory(error)}`,
          type: 'danger',
        })
      );
      dispatch(types.injectHours([hour]));
    }
  };
}

interface FetchHours {
  since: string;
  upto: string;
  userId?: number | null;
  userIds?: Array<number> | null;
}
export function fetchHours(
  { since, upto, userId, userIds }: FetchHours,
  options?: { key?: string; append?: boolean }
): Thunk {
  return async dispatch => {
    const request: Promise<Array<Hour<LightProject>>> =
      // Hours for multiple users goes through reporting API
      userIds && !!userIds.length
        ? getReportsSummaryHoursInChunks({
            since,
            until: upto,
            user_ids: userIds.join(','),
            scope: 'events',
            published: true,
            group_only: true,
          })
        : userId
          ? hoursApi.getHours({ since, upto, userId })
          : // No user filter: fetch all users' hours via reporting API
            getReportsSummaryHoursInChunks({
              since,
              until: upto,
              scope: 'events',
              published: true,
              group_only: true,
            });

    const hours = await dispatch(wrapFetching(request, options?.key || 'hours'));
    dispatch(types.fetchedHours(hours, { append: options?.append }));
    return hours;
  };
}

const getReportsSummaryHoursInChunks = (
  query: EntityDataQuery,
  page = 1
): Promise<Array<Hour<LightProject>>> => {
  const LIMIT = 500;
  return reportsApi
    .getReportHours({
      ...query,
      limit: LIMIT,
      page,
    })
    .then(hours => {
      if (hours.length >= LIMIT) {
        return getReportsSummaryHoursInChunks(query, page + 1).then(tail => hours.concat(tail));
      }
      return hours;
    });
};

export function updateProjectAfterHourSave(hour: Hour<Project>): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const projectById = getProjectById(state)[hour.project?.id];
    const project = projectById ? enrichProjectVariant(projectById, new Set()) : null;

    if (project != null && project.fullProject) {
      const { first_logged_on, last_logged_on } = project.fullProject;
      let updated_first_logged_on = first_logged_on;
      let updated_last_logged_on = last_logged_on;

      if (last_logged_on && isAfter(hour.day, last_logged_on)) {
        updated_last_logged_on = hour.day;
      }

      if (first_logged_on && !isAfter(hour.day, first_logged_on)) {
        updated_first_logged_on = hour.day;
      }

      dispatch(
        fetchedFullProjects([
          {
            ...project.fullProject,
            ...hour.project,
            first_logged_on: updated_first_logged_on,
            last_logged_on: updated_last_logged_on,
          },
        ])
      );
    }
  };
}

export function fetchHoursAfterPushNotification(
  ids: Array<number>,
  // only update what's currently in state
  options: { updateExistingOnly?: boolean } = { updateExistingOnly: false }
): Thunk<Promise<Array<number>>> {
  return async (dispatch, getState) => {
    const { updateExistingOnly } = options;
    const draft = getHourDraft(getState());
    try {
      const hours = await dispatch(
        wrapFetching(
          fetchInBatchesOf(50, ids, batch => hoursApi.getHourIds(batch)),
          types.SYNC_BACKGROUND_CHANGES_KEY
        )
      );
      const updatedDraftPayload = hours.find(h => h.id === draft?.id);
      if (draft && draft.id && updatedDraftPayload) {
        dispatch(
          types.updateHourDraft({
            ...updatedDraftPayload,
            project: updatedDraftPayload.project.id,
            _pusherUpdatedAt: new Date().toISOString(),
          })
        );
      }

      if (updateExistingOnly) {
        dispatch(types.updateHours(hours));
      } else {
        dispatch(types.injectHours(hours));
      }
    } catch (e) {
      dispatch(showToast({ message: errorMessageFactory(e), type: 'danger' }));
    }

    return ids;
  };
}

export function fetchHourIds(ids: Array<number>): Thunk<Promise<Array<number>>> {
  return async dispatch => {
    const hours = await dispatch(wrapFetching(hoursApi.getHourIds(ids)));
    dispatch(types.injectHours(hours));
    return ids;
  };
}
interface SaveHourOptions {
  restoreIgnoredMemories?: boolean;
  refreshForecastId?: number;
  createImmediately?: boolean;
  fallbackHour?: Hour<number>;
  hasChangedEntryIds?: boolean;
}
export function saveHour(
  hour: HourDraft,
  options: SaveHourOptions = {}
): Thunk<Promise<Hour<Project>>> {
  return async (dispatch, getState) => {
    const state = getState();
    const features = getAccountFeatures(state);
    const hasHourStatesFeature = !!features.hour_states;
    const hasBillingFeature = !!features.billing;
    // Save onboarding quest step
    dispatch(saveUserProperty(HAS_CREATED_HOUR, true));
    const existingHour = hour?.id ? getHourEntities(state)[hour?.id] : null;
    // Update local state of hours that have an id reference.
    const user = getUserEntities(state)[hour.user.id];
    const hourId = hour.id;

    const hourPayload = {
      ...hour,
      id: hourId || OPTIMISTIC_HOUR_IDENTIFIER,
      _localDraft: !hourId,
      user,
      project: hour.project!,
    };

    const optimisticHour = hourId || !options.createImmediately ? null : hourPayload;

    if (hourId) {
      dispatch(types.editHour(hourPayload));
    } else if (optimisticHour) {
      dispatch(types.addHour(optimisticHour));
    }

    let updatedHour: Hour<Project>;
    try {
      updatedHour = await dispatch(
        wrapSaving(
          hoursApi.saveHour(
            normalizedHourDraftToXhrFactory(
              hour,
              finalizeHourAnalyticsContext(hour, state),
              getIsUserAdmin(state),
              hasHourStatesFeature,
              hasBillingFeature
            )
          ),
          'hour'
        )
      );

      // Any time entry saved should queue a retraining of AI model
      if (getIsTicAiCurrentlyActive(state)) {
        dispatch(safelyDelayRetrainAiModel());
      }

      dispatch(updateProjectAfterHourSave(updatedHour));
      // The backend auto-joins the user to the picked project on save
      // (under ALL_PROJECTS_LOGGING). Mirror that in Redux so any other
      // locked hour on the same project unlocks immediately, without a
      // page refresh. insertInferredProjectPermissions only reads `id`
      // and `has_permission` off each entry, so a partial shape is safe.
      if (updatedHour.project && hour.user?.id) {
        dispatch(
          insertInferredProjectPermissions(hour.user.id, [
            { id: updatedHour.project.id, has_permission: true } as LightProject,
          ])
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          title: 'Unable to save entry',
          message: errorMessageFactory(error),
          type: 'danger',
        })
      );
      if (options.fallbackHour) {
        dispatch(types.editHour({ ...existingHour, ...options.fallbackHour }));
      }
      if (optimisticHour?._localDraft) {
        dispatch(types.deleteHour(hourPayload.id));
      }

      throw error;
    }

    if (optimisticHour?._localDraft) {
      dispatch(types.deleteHour(OPTIMISTIC_HOUR_IDENTIFIER));
    }
    // If hour has forecast_id, we must re-fetch the forecast to get it's latest progress/state.
    // This should be replaced by Pusher support for Forecasts, as there are use-cases not covered by
    // this approach (e.g, editing a time entry and changing the forecast_id will result in the
    // previous forecast not being updated.)
    const forecastId = options.refreshForecastId || hour.forecast_id;
    if (forecastId) {
      if (existingHour?.forecast_id && existingHour?.forecast_id !== forecastId) {
        dispatch(fetchForecastId(existingHour?.forecast_id));
      }
      dispatch(fetchForecastId(forecastId));
    }

    // If draft has id, it means we already have the hour in store
    if (hourId) {
      dispatch(types.editHour(updatedHour));
    } else {
      dispatch(types.addHour(updatedHour));
      // Dismiss the suggestion this was based off, if any:
      if (hour.suggestion_id) {
        dispatch(acceptSuggestionId(hour.suggestion_id));
      }
    }
    dispatch(types.savedHour());

    if (options.restoreIgnoredMemories) {
      await dispatch(ensureEntryIdsAreNotMarkedAsSpam(hour.entry_ids));
    }

    return updatedHour;
  };
}

interface SaveHourDraftOptions {
  clearDraft: boolean;
}
export function unlockHourDraft() {
  return types.unlockHourDraft();
}

export function saveHourDraft(
  partialDraft: Partial<HourDraft>,
  options: SaveHourDraftOptions = { clearDraft: false }
): Thunk<Promise<Hour<Project>>> {
  return async (dispatch, getState) => {
    const state = getState();
    const existingDraft = getHourDraft(state);
    if (!existingDraft) {
      throw new Error('No draft to save');
    }
    const { note } = getEnrichedHourDraft(state) ?? { note: '' };
    const existingHour = existingDraft?.id ? getHourEntities(state)[existingDraft.id] : undefined;
    const draft: HourDraft = { ...existingDraft, note, ...partialDraft };

    // Move project to top of "recently used"
    dispatch(
      touchProjectPermission({
        userId: draft.user.id,
        projectId: draft.project!,
      })
    );

    // Update local state at once if we already have an id reference
    if (draft.id && options.clearDraft) {
      dispatch(types.clearHourDraft({ preserveOriginal: true }));
    }

    // If draft has (or used to have) an hour.forecast_id association,
    // we must re-fetch the forecast to get it's latest progress/state.
    const refreshForecastId = draft.forecast_id || existingHour?.forecast_id || undefined;

    // saveHour deals with updating local state and settings errors,
    // so we'll only deal with the active hour draft
    let updatedHour: Hour<Project>;
    try {
      updatedHour = await dispatch(
        saveHour(draft, { restoreIgnoredMemories: true, refreshForecastId })
      );
      // make sure the hour note strategy is in sync with the user preference
      dispatch(resetAutoNoteStrategy());
      dispatch(resetGeneratedNotes());
      dispatch(types.clearHourDraft({ preserveOriginal: false }));
    } catch (e) {
      // Reinstate the draft if we error out
      dispatch(types.setHourDraft(draft));
      throw e;
    }
    // If this hour wasn't previously saved, accept suggestions and clear draft
    if (!draft.id) {
      // Clear the draft (hour.id drafts were cleared above)
      if (options.clearDraft) {
        dispatch(types.clearHourDraft());
      }
    }

    if (!options.clearDraft) {
      dispatch(types.updateHourDraft(hourDraftFactory(updatedHour, draft.context)));
    }
    return updatedHour;
  };
}

/**
 * Reset the hour draft to initial state, `{}`
 */
export function clearHourDraft(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const { hasErrors } = getHourXhrUIState(state);
    if (hasErrors && state.hours.draftOriginal) {
      dispatch(types.editHour(state.hours.draftOriginal));
    }
    dispatch(types.clearHourDraft());
    dispatch(resetGeneratedNotes());
  };
}

export function createHourDraftFromId(hourId: number, context: HourAnalyticsContext): Thunk {
  return (dispatch, getState) => {
    const hour = getUnprocessedHours(getState()).find(h => h.id === hourId);
    if (hour) {
      dispatch(createHourDraft(context, hour));
    }
  };
}

export function createHourDraftFromURLParams(context: HourAnalyticsContext): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const urlParameters = getUrlParameters(state); // from url or defaults
    const now = DateTime.now();
    const initialSeconds = !urlParameters.total_minutes ? 0 : urlParameters.total_minutes * 60;
    let from: string | null = null;
    if (urlParameters.start_time) {
      from = now
        .set({
          hour: urlParameters.start_time.hour,
          minute: urlParameters.start_time.minute,
        })
        .toISO();
    } else if (initialSeconds) {
      from = now.minus({ seconds: initialSeconds }).toISO();
    }

    const payload: HourDraftSeed = {
      day: urlParameters.date || now.toISODate(),
      from,
      duration: calculateDuration(initialSeconds),
    };

    dispatch(createHourDraft(context, payload));
  };
}

export const initializePermissionsAndProject = (
  projectId: number | null
): Thunk<Promise<unknown>> => {
  return async (dispatch, getState) => {
    if (!projectId) return;

    // read updated state including project permissions
    if (getShouldFetchProjectDetails(getState())) {
      await dispatch(
        fetchProjectIdSmartly(projectId, { needUsers: true, ignoreAccessDenied: true })
      );
    }
  };
};

export function updateHourDraft(patch: Partial<HourDraft>): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);

    let autoAssignProjectId: number | null = null;
    let linkedTaskUri: string | null = null;
    if (draft && patch.project == null && draft.project == null && patch.external_links?.length) {
      const [link] = patch.external_links;
      const tasks = getTicTasks(state);
      if (link && tasks) {
        linkedTaskUri = link.uri;
        const matchingTask = tasks.find(task => task.uri === link.uri);
        if (matchingTask?.timely_project_id) {
          autoAssignProjectId = matchingTask.timely_project_id;
        }
      }
    }

    const changes = hourUtils.synchronizeDurationAndTimestamps(draft!, patch);

    dispatch(
      types.updateHourDraft({
        ...changes,
        _shouldRegenerateNote: hourUtils.shouldRegenerateNoteForDraft(draft!, changes),
      })
    );

    if (autoAssignProjectId) {
      debug.template.hour('Auto assigning project from TIC task link', {
        autoAssignProjectId,
        linkedTaskUri,
        externalLinks: patch.external_links,
      });
      dispatch(setHourDraftProject(autoAssignProjectId));
    }
  };
}

/**
 * Updates the hour draft's note.
 */
export function updateHourDraftNote(note: string) {
  return types.updateHourDraft({
    // Maximum 2048 chars supported in database
    note: truncateText(note, MAX_HOUR_NOTE_CHARS),
  });
}

/**
 * Updates the hour draft, unless the draft is replaced or cleared out.
 */
export function updateHourDraftUnlessChanged(hour: Partial<HourDraft>): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) {
      return;
    }
    const draftCanBeUpdated = (!draft.id && draft.day) || draft.id === hour.id;

    if (draftCanBeUpdated) {
      dispatch(types.updateHourDraft(hour));
    } else {
      debug.warn('Server returned an hour draft, but draft has been replaced/cleared');
    }
  };
}

export function setHourTimestamp(
  hourId: number,
  atIndex: number,
  changes: Partial<HourTimestamp> | null,
  context: HourAnalyticsContext
): Thunk {
  return async (dispatch, getState) => {
    const state = getState();

    const hour = state.hours.entities[HOUR_SCHEMA_KEY][hourId];
    let updates: Partial<HourDraft> = {};

    if (!hour) {
      return;
    }

    const timestamps = updateTimestamp(hour.timestamps, atIndex, changes);
    updates = { ...hour, timestamps };

    const savePayload = {
      ...hour,
      ...updates,
      duration: calculateDurationFromTimestamps(timestamps),
      updated_at: dayTools.unix(),
      context,
    };

    await dispatch(saveHour(savePayload));
  };
}

/** Used by both Classic & TIC */
export function setHourDraftTimestamp(
  atIndex: number | null,
  changes: Partial<HourTimestamp> | null
): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);

    if (!draft) {
      return;
    }
    const timestamps = updateTimestamp(draft.timestamps, atIndex, changes);
    dispatch(updateHourDraft({ timestamps }));
  };
}

export type ToggleHourTimestampFromMemory = {
  from: string;
  to: string;
  /** memory.id's, but following Timely `hours` API convention */
  entry_ids: VariousMemoryIdRefs[];
};

/**
 * Toggles the timestamp range in on new or existing hour draft.
 *
 * @see TicMemoryTimelineItem#selectionOnMemory - The handler that triggers this function.
 * @link https://linear.app/timelyapp/issue/BUG-106 Relevant history if you're here to link `memory.id`'s
 *
 * @note The `note` passed as seed is ignored if existing draft/hour already has a note.
 */
export function toggleHourTimestampFromMemory(
  action: 'add' | 'remove',
  mutatingTimestamps: ToggleHourTimestampFromMemory[],
  hourDraftSeed: Partial<HourDraft> = {},
  interaction: HourAnalyticsContext['interaction'] = 'Memory Sets Timestamp',
  processTimestamps = true
): Thunk<void> {
  return (dispatch, getState) => {
    if (!mutatingTimestamps.length) {
      throw new Error('toggleHourTimestampFromMemory::No timestamps to mutate');
    }
    const state = getState();
    const currentDraft = getHourDraft(state);
    const existingHour = hourDraftSeed?.id ? getHourEntities(state)[hourDraftSeed.id] : null;

    if (mutatingTimestamps.length === 0) {
      debug.warn('Discarding empty timestamp mutation after snapping to nearest minute');
      return;
    }

    // Modify timestamps so they don't overlap.
    const baseTimestamps = currentDraft?.timestamps || existingHour?.timestamps || [];

    // Add or remove the timestamp from existing range
    let timestamps = baseTimestamps;
    if (processTimestamps) {
      // In timeline view, we want to combine timestamps as much as possible
      timestamps = mutateTimestampRange(action, mutatingTimestamps, baseTimestamps);
    } else {
      // In list view, we *never* want to combine timestamps. We leave all that work to the caller.
      if (action === 'add') {
        timestamps = [...baseTimestamps, ...mutatingTimestamps];
      } else {
        timestamps = baseTimestamps.filter(
          ts =>
            !mutatingTimestamps.some(m =>
              m.entry_ids.every(entryId => ts.entry_ids.includes(entryId))
            )
        );
      }
    }

    const day = getActualDate(state);
    const duration = calculateDurationFromTimestamps(timestamps);

    if (action === 'add') {
      // Add to existing draft
      if (currentDraft) {
        // Don't overwrite the existing draft's id - preserve it to avoid creating duplicates
        const { id: _seedId, ...seedWithoutId } = hourDraftSeed;

        // Re-derive billable when the project changes (TT-4519)
        // The seed's project (from a prediction/suggestion) may differ from the draft's project,
        // and the new project may have a different billable status.
        const newProjectId = seedWithoutId.project ?? currentDraft.project;
        let billableUpdate: { billable?: boolean } = {};
        if (newProjectId) {
          const projectVariant = getProjectVariantMap(state)[newProjectId];
          const fullProject = getFullProjectOrNull(projectVariant);
          if (fullProject) {
            billableUpdate = { billable: fullProject.billable };
          }
        }

        dispatch(
          updateHourDraft({
            ...seedWithoutId,
            // Leave existing note alone if already set
            note: currentDraft.note || hourDraftSeed.note,
            day,
            duration,
            timestamps,
            entry_ids: [],
            ...billableUpdate,
          })
        );
      }
      // Open & add to existing `hour` if seed `id` reference matches, otherwise create new draft
      else {
        const combinedDraftSeed = {
          ...existingHour,
          ...hourDraftSeed,
          // Leave existing note alone if already set
          note: existingHour?.note || hourDraftSeed?.note,
          day,
          duration,
          timestamps,
          entry_ids: [],
        };
        dispatch(createHourDraft({ interaction }, combinedDraftSeed));
      }
    }

    // When removing entry/tic memories from a draft...
    if (action === 'remove' && currentDraft) {
      dispatch(updateHourDraft({ ...hourDraftSeed, entry_ids: [], timestamps }));
    }
  };
}

export function setLastDirectlySelectedMemory(memoryId: number): Thunk {
  return dispatch => {
    dispatch(
      updateHourDraft({
        // create new object each selection to re-trigger effects even when selecting the same again
        _lastDirectlySelectedMemory: { id: memoryId, timestamp: DateTime.now().toISO() },
      })
    );
  };
}

/**
 * Batch add or remove `hour.timestamps` for every memory that belongs to tool.id/raw reference
 */
export function batchMutateTimestampsFromTool(
  action: 'add' | 'remove',
  payload: {
    tool?: Memory['tool'];
  }
): Thunk<void> {
  return (dispatch, getState) => {
    const { tool } = payload;
    const memories = getTicAndLegacyMemoriesAsMemoryGroups(getState());

    if (!tool || (!tool.id && !tool?.raw) || !memories.length) {
      debug.info('batchMutateTimestampsFromTool::No tool or memories to mutate');
      return;
    }

    const mutatingTimestamps = memories.reduce((acc, memory) => {
      const matchToolId = !!tool.id && memory.tool?.id === tool.id;
      const matchToolRaw = !!tool.raw && memory.tool?.raw === tool.raw;
      if (matchToolId || matchToolRaw) {
        acc.push({
          from: memory.started_at,
          to: memory.ended_at,
          entry_ids: [memory.v_id ?? `${memory.id}`],
        });
      }
      return acc;
    }, [] as ToggleHourTimestampFromMemory[]);

    dispatch(toggleHourTimestampFromMemory(action, mutatingTimestamps));
  };
}

/**
 * Inserts, updates or removes a timestamp while automatically removing any overlappings.
 */
function updateTimestamp(
  timestamps: ReadonlyArray<HourTimestamp>,
  atIndex: number | null,
  changes: Partial<HourTimestamp> | null
): Array<HourTimestamp> {
  const modified = [...timestamps];

  if (changes === null) {
    if (atIndex !== null) {
      modified.splice(atIndex, 1);
    }
  } else if (atIndex !== null) {
    const currentValue = timestamps[atIndex];
    const newValue = { ...currentValue, ...changes };
    modified[atIndex] = newValue;
  } else if (changes.from && changes.to) {
    // Todo: Can probably push `changes` directly to `modified` array
    modified.push({ from: changes.from, to: changes.to, entry_ids: changes.entry_ids || [] });
  }

  return mergeOverlappingTimestampsContainingTicMemories(modified);
}

export type HourDraftSeed = Merge<
  Partial<HourDraft>,
  {
    day: string;
  }
>;

export function createHourDraft(
  context: HourAnalyticsContext,
  hourSeed: HourDraftSeed = { day: '' }
): Thunk {
  return async (dispatch, getState) => {
    const state = getState();

    // Check if subscription is expired or trial ended
    if (getIsSubscriptionExpiredOrTrialEnded(state)) {
      dispatch(showAccountExpiredMessage());
      return;
    }

    let fullProject = hourSeed.project
      ? await dispatch(
          fetchProjectIdSmartly(hourSeed.project, {
            needUsers: true,
            ignoreAccessDenied: true,
          })
        )
      : undefined;

    // If fetchProjectIdSmartly bailed (in-flight or rate-limited),
    // check the store — the parallel prefetch may have completed.
    if (!fullProject && hourSeed.project) {
      const variant = getProjectVariantMap(getState())[hourSeed.project];
      if (variant?.type === 'full') {
        fullProject = variant.project;
      }
    }

    // Determine billable status (TT-4519):
    // - Non-billable project always forces false (the UI hides the billable toggle)
    // - Billable project respects per-entry override from hourSeed (user can mark individual entries non-billable)
    // - No project data: fall back to hourSeed or default true
    const billable =
      fullProject?.billable === false
        ? false
        : (hourSeed.billable ?? fullProject?.billable ?? true);

    const currency = fullProject?.currency ?? getAccountCurrency(state);

    // Determine sequence
    const hoursInDay = getHours(state).filter(({ day }) => day === hourSeed.day);
    const sequence = positionOrHighest(null, hoursInDay);
    // Determine user id: Can be overwritten in hourSeed.
    const userId = getCalendarUserId(state);

    // Avoid attaching hour.entry_ids in TIC/EMD mode.
    let entryIds: number[] = [];
    if (!getHasEnabledTic(state)) {
      entryIds = getTimelineSelection(state).entryIds;
    }

    const user = { id: userId };

    const draft: HourDraft = hourDraftFactory(
      {
        id: null,
        user,
        sequence,
        entry_ids: entryIds,
        _currency: currency,
        ...hourSeed,
        // billable MUST come after ...hourSeed to prevent the seed's stale
        // billable value from overriding the project-derived value (TT-4519)
        billable,
      },
      context
    );

    if (getHasHourStatesFeature(state)) {
      if (draft.state !== null) {
        const userRoleId = getUserRoleId(state);

        // Use state from store to get up-to-date permissions
        const updatedState = getHourStateById(state, draft.state.id);
        if (updatedState) {
          draft.state = updatedState;
        }

        const userLedUserIds = getUserLedUserIds(state);
        const userIsTeamLead = userLedUserIds.has(user.id);

        draft.manage &&= draft.state.permissions.some(
          p => p.role_id === userRoleId || (p.team_lead && userIsTeamLead)
        );

        // Auto-soft-unlock when starting the draft from an unlocked state, so the draft doesn't
        // become locked mid-editing when changing state
        if (!hourUtils.isHourLocked(draft.state, { userRoleId, userIsTeamLead })) {
          draft._unlockedByUser = true;
        }
      } else {
        draft._unlockedByUser = true;
      }
    }

    dispatch(resetAutoNoteStrategy());
    dispatch(types.setHourDraft(draft));

    // If we move Memory Inbox selection to draft, clear that selection:
    if (entryIds.length && hourSeed.entry_ids == null) {
      dispatch(clearSelectEntryIds());
    }
  };
}

export function resetAutoNoteStrategy(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const strategy = getPreferredAutoNoteStrategy(state);
    dispatch(types.setAutoNoteStrategy(strategy));
  };
}

export function resetGeneratedNotes(): Thunk {
  return dispatch => {
    dispatch(types.setGeneratedNotes(null));
  };
}

export function deleteHourDraft(): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);

    if (!draft) return;

    if (draft.id) {
      await dispatch(deleteHourId(draft.id));
      if (draft.forecast_id) {
        dispatch(fetchForecastId(draft.forecast_id));
      }
    }

    dispatch(clearHourDraft());
  };
}

export interface SetHourDraftSuggestedDuration {
  seconds: number;
}

export function setHourDraftSuggestedDuration({ seconds }: SetHourDraftSuggestedDuration) {
  return updateHourDraft({ duration: calculateDuration(seconds) });
}

export function setHourDraftDuration({
  hours,
  minutes,
  section,
}: {
  hours: number;
  minutes: number;
  section: 'logged' | 'estimated';
}): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) {
      return;
    }

    const totalSeconds = Math.min(24, hours) * 60 * 60 + Math.min(60, minutes) * 60;
    const duration = calculateDuration(totalSeconds);

    const draftChanges: Partial<HourDraft> =
      section === 'estimated'
        ? {
            estimated_cost: calculateCost(duration.total_hours, draft.hour_rate, draft._currency),
            estimated_duration: duration,
          }
        : {
            cost: calculateCost(duration.total_hours, draft.hour_rate, draft._currency),
            duration,
          };

    dispatch(updateHourDraft(draftChanges));
  };
}

export function adjustHourDraftDuration(payload: number): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) {
      return;
    }
    const { durationForMode } = getHourEditorDurationSuggestions(state);
    const newTotal = durationForMode.total_hours - payload;
    const hoursAsSeconds = Math.floor(newTotal * 60 * 60);
    const duration = calculateDuration(hoursAsSeconds);

    const draftChanges: Partial<HourDraft> = {
      cost: calculateCost(durationForMode.total_hours, draft.hour_rate, draft._currency),
      duration,
    };
    dispatch(updateHourDraft(draftChanges));
  };
}

export function clearHourDraftDuration(key: 'duration' | 'estimated_duration') {
  return updateHourDraft({
    [key]: calculateDuration(0),
  });
}

export interface SetHourDuration {
  hourId: number;
  seconds: number;
  isPlanned: boolean;
}
export function setHourDuration(
  payload: SetHourDuration,
  context: HourAnalyticsContext
): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const hour = state.hours.entities[HOUR_SCHEMA_KEY][payload.hourId];
    if (!hour) {
      return;
    }

    const currencyLookup = getCurrenciesById(state);

    // Prepare payload for server-side save:
    const duration = calculateDuration(payload.seconds);
    const cost = calculateCost(
      duration.total_hours,
      hour.hour_rate,
      currencyLookup[hour.cost.currency_code]
    );
    const timestamps = adjustTimestampsToDuration(hour.timestamps, payload.seconds);

    const savePayload = {
      ...hour,
      timestamps,
      duration: payload.isPlanned ? hour.duration : duration,
      estimated_duration: payload.isPlanned ? duration : hour.estimated_duration,
      cost: payload.isPlanned ? hour.cost : cost,
      estimated_cost: payload.isPlanned ? cost : hour.estimated_cost,
      updated_at: dayTools.unix(),
      isPlanned: payload.isPlanned,
      context: { ...context, created_from: null },
    };

    await dispatch(saveHour(savePayload));
  };
}

type ProjectReference = null | number;
/**
 * Sets the draft's project
 * DISABLED for now, but could also select the memories most likely to relate to this project
 * Will need tweaking before re-enabling, if ever.
 */
export function setHourDraftProject(projectId: ProjectReference): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!projectId || !draft) {
      return;
    }

    // Verify user has access to this project. When ALL_PROJECTS_LOGGING is on
    // we skip this gate — the backend auto-joins the user on save.
    const actingUserId = draft.user?.id ?? state.user.id;
    if (!getAllProjectsLoggingEnabled(state)) {
      const permissionsForUser = getProjectPermissionsMap(state)[actingUserId];
      if (permissionsForUser?.length && !permissionsForUser.includes(projectId)) {
        debug.template.hour('Skipping setHourDraftProject due to missing project access', {
          projectId,
          actingUserId,
        });
        return;
      }
    }

    const variant = getProjectVariantMap(state)[projectId];

    // If we don't have the project fully loaded, set it immediatly
    // then fetch details & verify we respect label_ids rules.
    if (variant?.type !== 'full') {
      dispatch(types.updateHourDraft({ project: projectId }));
      return dispatch(fetchProjectId(projectId)).then(loadedProject => {
        if (!loadedProject) {
          return;
        }

        const patch: Partial<HourDraft> = {
          label_ids: removeIllegalLabelIds(loadedProject, draft.label_ids),
          billable: loadedProject.billable,
          _currency: loadedProject.currency,
        };

        return dispatch(types.updateHourDraft(patch));
      });
    }

    const patch: Partial<HourDraft> = {
      project: projectId,
      label_ids: removeIllegalLabelIds(variant.project, draft.label_ids),
      billable: variant.project.billable,
      _currency: variant.project.currency,
    };

    dispatch(types.updateHourDraft(patch));
  };
}
/**
 * Chooses a forecast and (optionally) updates logged/estimated time.
 */
export function setHourDraftForecastId(
  forecastId?: number,
  smartlySetDuration: boolean = true
): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const payload: Partial<HourDraft> = { forecast_id: forecastId || null };
    const userId = getHourDraftUserId(state);

    if (forecastId && userId) {
      const forecast = getEnrichedForecasts(state).find(({ id }) => id === forecastId);
      payload.label_ids = forecast?.label_ids || [];

      // Autofill logged or planned time if currently 00:00. Beware that HourEditor
      // will respond by turning on planned time controls via componentDidUpdate
      const currentDraft = getHourDraft(state);
      if (forecast && currentDraft && smartlySetDuration) {
        const draftDurationMode = getDraftDurationMode(state);

        // Days in the future will set estimate, today+past will set logged.
        const isInFuture = isAfter(currentDraft?.day, new Date().toISOString());
        const typeOfTimeInputToAdjust = isInFuture ? 'estimated' : 'logged';
        const existingDurationMins = isInFuture
          ? currentDraft?.estimated_duration?.total_minutes
          : currentDraft?.duration?.total_minutes;

        if (draftDurationMode === 'manual' && existingDurationMins < 1) {
          const seconds =
            (forecast.allocationByUser[userId]?.dailyWorkMinutesRequiredToCompleteWithinRange ??
              0) * 60;
          const { hours, minutes } = calculateDuration(seconds);
          dispatch(setHourDraftDuration({ hours, minutes, section: typeOfTimeInputToAdjust }));
        }
      }
    }

    dispatch(types.updateHourDraft(payload));
  };
}

export function setHourDraftTags(labelIds: Array<number> = []) {
  return types.updateHourDraft({
    label_ids: labelIds,
  });
}

/**
 * Refresh any hours that had a running timer (for the current calendarUserId user)
 */
export function refreshHoursWithRunningTimers(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const userId = getCalendarUserId(state);
    const hourEntities = getHourEntities(state);
    const hourResult = getHourResult(state);

    const hoursToUpdate = hourResult.filter(hourId => {
      const hour = hourEntities[hourId];
      return hour?.timer_state === 'start' && userId === hour?.user.id;
    });

    if (hoursToUpdate.length > 0) {
      dispatch(fetchHourIds(hoursToUpdate));
    }
  };
}

export function toggleTimerOnHourId(hourId: number, context: HourAnalyticsContext): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const hour = getHourEntities(state)[hourId];
    if (!hour) {
      return;
    }

    const draft = hourDraftFactory(hour, context);
    const { setTimerState, saveHourPayload } = getToggleTimerChanges(state, draft);

    let updatedHour: Hour<Project>;
    try {
      updatedHour = await dispatch(
        wrapSaving(hoursApi.saveHour(saveHourPayload, { setTimerState }), 'hour')
      );
      dispatch(updateProjectAfterHourSave(updatedHour));
      dispatch(types.editHour(updatedHour));

      if (setTimerState === 'stop') {
        // The response of /hours/:id/stop does not include any changes made on timestamps. Let's refresh the hour.
        dispatch(fetchHourIds([updatedHour.id]));
      } else {
        dispatch(refreshHoursWithRunningTimers());
      }
    } catch (error) {
      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      throw error;
    }
  };
}

export function updateHourDuration(
  hourId: number,
  seconds: number,
  context: HourAnalyticsContext
): Thunk {
  return async (dispatch, getState) => {
    const state = getState();
    const hour = getHourEntities(state)[hourId];
    if (!hour) {
      return;
    }
    const draft = hourDraftFactory(hour, context);
    const draftUpdates = {
      ...draft,
      duration: calculateDuration(seconds),
      timestamps: adjustTimestampsToDuration(draft.timestamps, seconds),
    };
    const saveHourPayload = normalizedHourDraftToXhrFactory(
      draftUpdates,
      finalizeHourAnalyticsContext(draftUpdates, state)
    );
    const request = hoursApi.saveHour(saveHourPayload);
    let updatedHour: Hour<Project>;
    try {
      updatedHour = await dispatch(wrapSaving(request, 'hour'));
      dispatch(updateProjectAfterHourSave(updatedHour));
    } catch (error) {
      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      throw error;
    }
    dispatch(types.editHour(updatedHour));
  };
}

/**
 * Starts or stops the timer on the Hour draft.
 * Local hour slice is updated, as well as the draft itself. When timer is started without
 * project, it'll be automatically assigned.
 *
 * NB: This is fairly shitty code, but intended as a temporary workaround while we
 * wait for better API support, ref
 * - https://app.clubhouse.io/timelyapp/story/8016/add-support-for-new-attribute-timer-state
 * - https://github.com/Timely/timely/pull/424
 */
export function toggleTimerOnHourDraft(): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) {
      return;
    }

    const { setTimerState, saveHourPayload } = getToggleTimerChanges(state, draft);

    // we need to save the hour first, whether it's a draft of a new or existing hour
    // if it's a new draft, the hour needs to exist on the server before we can start a timer on it
    // if it isn't, we need to sync any local changes to the server first to prevent things being out of sync below
    const request = hoursApi.saveHour(saveHourPayload).then(({ id }) => {
      return hoursApi.saveHour({ ...saveHourPayload, id }, { setTimerState });
    });

    let updatedHour: Hour<Project>;
    try {
      // Start the timer on the draft, and notify the server
      updatedHour = await dispatch(wrapSaving(request, 'hour'));
    } catch (error) {
      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      throw error;
    }

    // If we just accepted a suggestion, discard it locally
    if (draft.suggestion_id) {
      dispatch(acceptSuggestionId(draft.suggestion_id));
    }

    // Update the timer related properties on the draft
    // unless draft has been changed while saving was going on
    dispatch(
      updateHourDraftUnlessChanged({
        ...updatedHour,
        project: updatedHour.project.id,
      })
    );

    // dispatch({ type: !draft.id ? actionTypes.ADD_HOUR: actionTypes.EDIT_HOUR, payload: data });
    // Insert the hour in the store, if it wasn't there already
    if (!draft.id) {
      dispatch(types.addHour(updatedHour));
      // Update the hour in store
    } else {
      dispatch(types.editHour(updatedHour));
    }

    if (setTimerState === 'stop') {
      // The response of /hours/:id/stop does not include any changes made on timestamps. Let's refresh the hour.
      dispatch(fetchHourIds([updatedHour.id]));
    } else {
      dispatch(refreshHoursWithRunningTimers());
    }
  };
}

export function toggleTimestampOnHourDraft(): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) {
      return;
    }

    if (draft.from || draft.timestamps.length > 0) {
      dispatch(updateHourDraft({ from: null, to: null, timestamps: [] }));
    } else {
      const { day } = draft;
      // Duration on HourDraft is calculated based on mode & memories
      const { durationForMode } = getHourEditorDurationSuggestions(state);
      const selectedEntries = getSelectedMemoryEntriesChronologically(state);
      const countSecondBack = durationForMode.total_seconds || 0;

      const selectedEntry = selectedEntries.length ? selectedEntries[0] : null;

      const from =
        selectedEntry?.from ||
        fromISO(getCurrentMomentOnDay(day))
          .minus({ seconds: countSecondBack })
          .startOf('minute')
          .toISO();

      const to = fromISO(from).plus({ seconds: draft.duration.total_seconds }).toISO();

      dispatch(
        updateHourDraft({
          timestamps: [{ from, to, entry_ids: [] }],
        })
      );
    }
  };
}

/**
 * Adds or removes `hour.timestamps[].entry_ids[]` from the draft.
 *
 * Used by TIC memories only.
 */
export function toggleTicMemoryIdOnHourDraftTimestamp(
  timestampIndex: number,
  entryId: number
): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) return null;

    const updatedTimestamps = [...draft.timestamps];
    const timestamp = draft?.timestamps[timestampIndex];

    if (timestamp) {
      const updatedTimestamp = { ...timestamp, entry_ids: xor(timestamp.entry_ids, [entryId]) };
      updatedTimestamps.splice(timestampIndex, 1, updatedTimestamp);
    }

    dispatch(updateHourDraft({ timestamps: updatedTimestamps }));
  };
}

export function moveOrCopyHour(
  payload: MoveOrCopyHourPayload,
  context: HourAnalyticsContext
): Thunk {
  return (dispatch, getState) => {
    const state = getState();
    const hourEntities = getHourEntities(state);
    const hours = getUnprocessedHours(state);

    const source = hourEntities[payload.sourceHour.id];
    const target = payload.targetHour && hourEntities[payload.targetHour.id];
    const targetDay = payload.date;

    if (!source) return;

    if (payload.userId && !target) {
      let hour: HourDraft = {
        ...source,
        user: getUserEntities(state)[payload.userId],
        context,
      };

      // Without an id, a new hour will be created
      if (payload.dropEffect === 'copy') {
        hour.id = null;
        // Copying an hour should not copy the state.
        hour = resetHourStateProps(hour);
      }

      dispatch(
        saveHour(
          {
            ...hour,
            day: targetDay,
            user: getUserEntities(state)[payload.userId],
            context,
          },
          { fallbackHour: source, createImmediately: !payload.updateOptimistically }
        )
      );
      return;
    }

    // skip if they're the same
    if ((source && source.id) === (target && target.id)) {
      return;
    }

    const sourceDay = source.day;

    // skip if there's no target and we're not changing days
    if (sourceDay === targetDay && !target && payload.dropEffect !== 'copy') {
      return;
    }

    const copyOrMovedToSameDay = sourceDay === targetDay;
    const position = (target && target.sequence) || null;

    const updatedHours = hoursResequencer({
      dropEffect: payload.dropEffect ?? 'move',
      source,
      position,
      sourceDay,
      targetDay,
      hours,
      newUserId: payload.userId,
    });
    const sequencedHour = updatedHours.hour;
    const user = payload.userId ? getUserEntities(state)[payload.userId] : source.user;

    let hour: HourDraft = {
      ...source,
      day: sequencedHour.day,
      from: sequencedHour.from,
      to: sequencedHour.to,
      timestamps: sequencedHour.timestamps,
      sequence: sequencedHour.sequence,
      // Clear memory/suggestion relations to avoid invalid relationships
      // and potentially avoid AI confusion.
      suggestion_id: null,
      entry_ids: copyOrMovedToSameDay ? sequencedHour.entry_ids : [],
      _localDraft: payload.dropEffect === 'copy',
      user,
      context,
    };

    // Without an id, a new hour will be created.
    if (payload.dropEffect === 'copy') {
      hour.id = null;
      // Copying an hour should not copy the state.
      hour = resetHourStateProps(hour);
    }

    dispatch(types.injectHours(updatedHours.list));
    dispatch(
      saveHour(hour, { fallbackHour: source, createImmediately: !payload.updateOptimistically })
    );
  };
}

interface BulkCopyHours {
  hourIds: Array<number>;
  targetDays: Array<string>;
}

/**
 * Make copies of hourIds for targetDays (YYYY-MM-DD)
 * And automatically load those hours when successful.
 */
export function bulkCopyHours(
  { hourIds, targetDays }: BulkCopyHours,
  context: HourAnalyticsContext
): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    const hourEntities = getHourEntities(state);

    if (!hourEntities) {
      dispatch(showToast({ message: 'Something went wrong, please try again', type: 'danger' }));
      return Promise.reject(new Error('hourEntities undefined'));
    }

    const hoursToCopy = hourIds.map(id => hourEntities[id] || null).filter(notNull);

    const savePayload = targetDays.reduce(
      (prevHours: Array<NormalizedHourToApi>, day: string): Array<NormalizedHourToApi> => {
        const hours = hoursToCopy.map(hour => {
          const hourWithDefaultState = resetHourStateProps<Hour<number>>(hour);
          const draftWithDefaultState = draft ? resetHourStateProps<HourDraft>(draft) : draft;
          return updateHourTimes({
            hour: hourWithDefaultState,
            draft: draftWithDefaultState,
            day,
            context,
            state,
          });
        });

        return [...prevHours, ...hours];
      },
      []
    );

    // To trigger redirection from TimesheetPage to Calendar. Same as move hour.
    if (targetDays.length > 0) {
      getSetCalendarDate().then(setCalendarDate => dispatch(setCalendarDate(targetDays[0])));
    }

    return hoursApi
      .bulkCopyHours(savePayload)
      .then(data => {
        if (data.created_ids.length) {
          dispatch(
            showToast({
              message: `${pluralize(data.created_ids.length, {
                singular: 'Hour',
                plural: 'Hours',
              })} copied`,
              type: 'success',
            })
          );

          debug.template.ai('bulkCopyHours does NOT trigger retrainAiModel', {
            because: 'We cannot trust any timestamps or entry_ids anyway',
          });
        }
        return data;
      })
      .catch(error => {
        const message = errorMessageFactory(error);
        dispatch(showToast({ message, type: 'danger' }));
      });
  };
}

function updateHourTimes(props: {
  hour: Hour<number>;
  draft?: HourDraft;
  day: string;
  context: HourAnalyticsContext;
  state: IState;
}) {
  const { hour, draft, day, context, state } = props;
  const actualHour = hour.id === draft?.id ? draft : hour;
  const isSameDay = DateTime.fromISO(day).hasSame(DateTime.fromISO(actualHour.day), 'day');

  let payload = { ...actualHour, day };

  if (!isSameDay) {
    const hourWithUpdatedTimes = hourUtils.changeDay(actualHour, day);
    payload = {
      ...payload,
      ...hourWithUpdatedTimes,
      entry_ids: [],
    };
  }

  return normalizedHourDraftToXhrFactory(
    payload,
    finalizeHourAnalyticsContext({ ...payload, context }, state)
  );
}

export function bulkMoveHours(
  { hourIds, targetDays }: BulkCopyHours,
  context: HourAnalyticsContext
): Thunk<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState();
    const hourEntities = getHourEntities(state);
    const draft = getHourDraft(state);
    const hoursToMove = hourIds
      .map(id => hourEntities[id] || null)
      .filter(h => h !== null && !h.locked && !h.billed);

    const savePayload = targetDays.reduce(
      (prevHours: Array<NormalizedHourToApi>, day: string): Array<NormalizedHourToApi> => {
        const hours = hoursToMove.map(hour => {
          return updateHourTimes({ hour, day, context, state });
        });

        return [...prevHours, ...hours];
      },
      []
    );

    if (draft) {
      getMoveHourDraftToDate().then(moveHourDraftToDate =>
        dispatch(moveHourDraftToDate(targetDays[0]))
      );
    }

    return hoursApi
      .bulkUpdateHours(savePayload)
      .then(data => {
        if (data.updated_ids.length) {
          dispatch(
            showToast({
              message: `${pluralize(data.updated_ids.length, {
                singular: 'Hour',
                plural: 'Hours',
              })} moved`,
              type: 'success',
            })
          );

          debug.template.ai('bulkMoveHours does NOT trigger retrainAiModel', {
            because: 'We cannot trust any timestamps or entry_ids anyway',
          });
        }
        return data;
      })
      .catch(error => {
        const message = errorMessageFactory(error);
        dispatch(showToast({ message, type: 'danger' }));
      });
  };
}
/**
 * Creates or updates the HourDraft[]'s on Timely's bulk API,
 * then re/fetches the hours that got created/updated.
 */
export const bulkSaveHourDraftsThenReload = createAsyncThunk<
  Promise<BulkUpdateResponse>,
  any,
  { state: IState; dispatch: ThunkDispatch<IState, any, Action> }
>(
  'hours/bulkSaveHourDraftsThenReload',
  async (hoursDrafts: HourDraft[], { getState, dispatch }) => {
    const state = getState();

    const payload = hoursDrafts.map(hourDraft =>
      normalizedHourDraftToXhrFactory(hourDraft, finalizeHourAnalyticsContext(hourDraft, state))
    );

    try {
      const data = await hoursApi.bulkSaveHours(payload);
      const changed_ids = data.created_ids.concat(data.updated_ids);
      if (changed_ids.length > 0) {
        // Reload the hours that got created/updated
        // await the re-fetch to externally kept state feels right.
        await dispatch(fetchHourIds(changed_ids));

        // Any time entry saved should queue a retraining of AI model
        if (getIsTicAiCurrentlyActive(state)) {
          dispatch(safelyDelayRetrainAiModel());
        }
      }
      return data;
    } catch (error) {
      const message = errorMessageFactory(error);
      dispatch(showToast({ message, type: 'danger' }));
      throw error;
    }
  }
);

export function fetchLoggedHoursTotalForDraft(): Thunk<Promise<unknown>> {
  return async (dispatch, getState) => {
    const state = getState();
    const draft = getHourDraft(state);
    if (!draft) {
      return;
    }

    const draftDay = fromISO(draft.day);
    const since = draftDay.startOf('week').toISO();
    const until = draftDay.endOf('week').toISO();
    const userIds = [draft.user.id];
    const teamIds: Array<number> = [];
    const summary = await summaryApi.getUsersSummaryTotals({
      since,
      until,
      userIds,
      teamIds,
      totals: true,
      hasAccessToTasks: false, // Don't care about tasks here
    });
    const totalHours = summary.duration.total_hours.toFixed(2);
    dispatch(types.setLoggedWeeklyTotal(+totalHours));
  };
}

export function toggleHourHistoryModal() {
  return types.toggleHourHistoryModal();
}

export const batchCreateHours =
  (template: HourDraft): Thunk<Promise<Array<number>>> =>
  async (dispatch, getState) => {
    const { user_ids, ...hour } = template;
    if (!user_ids?.length) {
      return [];
    }

    const state = getState();
    const projects = getProjectById(state);
    const project = getFullProjectOrNull(projects[hour.project!]);
    if (hour.label_ids?.length && project?.enable_labels === TagSettings.None) {
      hour.label_ids = [];
    }

    const hours = user_ids.map(id =>
      normalizedHourDraftToXhrFactory(
        { ...hour, user: { id }, user_ids: [] },
        finalizeHourAnalyticsContext(hour, state)
      )
    );

    try {
      const response = await dispatch(wrapSaving(hoursApi.bulkCopyHours(hours)));

      if (response.created_ids.length > 0) {
        await dispatch(fetchHourIds(response.created_ids));
      } else {
        dispatch(
          showToast({
            type: 'danger',
            message: `An error occurred while creating the entries`,
          })
        );
      }

      TimelyAnalytics.track(BATCH_CREATED_PLANNED_ENTRIES, { count: user_ids.length });

      return response.created_ids;
    } catch (e) {
      dispatch(
        showToast({
          type: 'danger',
          message: `An error occurred while creating the entries`,
        })
      );
      console.error('batchCreateHours error', e);
      return [];
    }
  };

export const batchCreateHourDraft =
  (day: string): Thunk =>
  (dispatch, getState) => {
    // Check if subscription is expired or trial ended
    if (getIsSubscriptionExpiredOrTrialEnded(getState())) {
      dispatch(showAccountExpiredMessage());
      return;
    }

    dispatch(
      types.batchCreateHourDraft({
        day,
        context: { interaction: 'Batch Create Planned Entries' },
      })
    );
  };

export function fetchHoursInBatches(batches: Array<Partial<ForecastFilter>>): Thunk<Promise<void>> {
  return async dispatch => {
    try {
      const hourFetchRequests = batches
        .map(part =>
          Array.isArray(part.user_ids) &&
          part.user_ids.length > 0 &&
          part.since &&
          part.until &&
          part.until >= TODAY
            ? dispatch(
                fetchHours(
                  {
                    since: part.since,
                    upto: part.until,
                    userIds: part.user_ids.filter(notNull),
                  },
                  {
                    append: true,
                  }
                )
              )
            : null
        )
        .filter(Boolean);
      await dispatch(wrapFetching(Promise.all(hourFetchRequests)));
    } catch (e) {
      const message = errorMessageFactory(e);
      dispatch(showToast({ message, type: 'danger' }));
      throw e;
    }
  };
}
